import { useEffect, useState } from "react";
import { TrendingUp, Calendar, Users, Loader2 } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

const BUCKETS = [
  { v: "day", l: "Daily" },
  { v: "week", l: "Weekly" },
  { v: "month", l: "Monthly" },
];

function BarRow({ label, value, max, color = "#006E9E" }) {
  const pct = max > 0 ? (value / max) * 100 : 0;
  return (
    <div className="grid grid-cols-[140px_1fr_50px] gap-2 items-center text-sm" data-testid={`bar-${label}`}>
      <div className="font-mono text-xs text-gray-700 truncate">{label}</div>
      <div className="bg-gray-100 h-5 relative overflow-hidden">
        <div className="h-full transition-all duration-700"
             style={{ width: `${pct}%`, background: color }} />
      </div>
      <div className="font-mono font-bold text-right">{value}</div>
    </div>
  );
}

export default function WashThroughput() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bucket, setBucket] = useState("month");
  const [months, setMonths] = useState(6);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ bucket, months_back: String(months) });
      const { data: d } = await api.get(`/wash/throughput?${params.toString()}`);
      setData(d);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [bucket, months]);

  const buckets = data?.buckets || [];
  const customers = data?.by_customer || [];
  const maxBucket = Math.max(1, ...buckets.map((b) => b.total));
  const maxCust = Math.max(1, ...customers.slice(0, 10).map((c) => c.count));
  const aging = data?.aging;

  return (
    <div className="space-y-5" data-testid="wash-throughput">
      <div className="flex flex-wrap items-end gap-3 bg-gray-50 border border-gray-200 p-4">
        <div>
          <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Bucket</label>
          <div className="flex gap-1">
            {BUCKETS.map((b) => (
              <button
                key={b.v}
                onClick={() => setBucket(b.v)}
                className={`px-3 py-2 border text-sm transition-colors ${bucket === b.v ? "bg-[#006E9E] text-white border-[#006E9E]" : "border-gray-200 hover:border-gray-400"}`}
                data-testid={`bucket-${b.v}`}
              >{b.l}</button>
            ))}
          </div>
        </div>
        <div>
          <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Lookback (months)</label>
          <input
            type="number" min={1} max={24}
            value={months}
            onChange={(e) => setMonths(Math.max(1, Math.min(24, parseInt(e.target.value || "1", 10))))}
            className="ps-input font-mono w-24"
            data-testid="lookback-months"
          />
        </div>
        <div className="text-xs text-gray-500 ml-auto">
          {loading
            ? <span className="inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Loading…</span>
            : data && <>{data.total} certified/historical wash{data.total === 1 ? "" : "es"} in last {months} months</>}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="border border-gray-200 bg-white p-4">
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500 flex items-center gap-1">
            <TrendingUp className="w-3 h-3" /> Total washes
          </div>
          <div className="font-mono font-black text-3xl mt-1">{data?.total ?? "—"}</div>
        </div>
        <div className="border border-gray-200 bg-white p-4">
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500 flex items-center gap-1">
            <Calendar className="w-3 h-3" /> Buckets
          </div>
          <div className="font-mono font-black text-3xl mt-1">{buckets.length}</div>
        </div>
        <div className="border border-gray-200 bg-white p-4">
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500 flex items-center gap-1">
            <Users className="w-3 h-3" /> Customers
          </div>
          <div className="font-mono font-black text-3xl mt-1">{customers.length}</div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        <div className="border border-gray-200 bg-white p-5">
          <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-gray-600 mb-4">
            Throughput per {bucket}
          </div>
          {buckets.length === 0
            ? <div className="text-gray-400 text-sm py-6 text-center">No data in range.</div>
            : <div className="space-y-2">
                {buckets.map((b) => <BarRow key={b.label} label={b.label} value={b.total} max={maxBucket} />)}
              </div>}
        </div>

        <div className="border border-gray-200 bg-white p-5">
          <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-gray-600 mb-4">
            Top customers (cleaned-for)
          </div>
          {customers.length === 0
            ? <div className="text-gray-400 text-sm py-6 text-center">No data in range.</div>
            : <div className="space-y-2">
                {customers.slice(0, 15).map((c) => (
                  <BarRow key={c.customer} label={c.customer} value={c.count} max={maxCust} color="#99D420" />
                ))}
                {customers.length > 15 && (
                  <div className="text-xs text-gray-400 text-center pt-2">+ {customers.length - 15} more</div>
                )}
              </div>}
        </div>
      </div>

      <div className="border border-gray-200 bg-white p-5">
        <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-gray-600 mb-3">
          Aging — Requested → Certified (average hours)
        </div>
        {aging?.avg_hours_req_to_cert == null ? (
          <div className="text-gray-400 text-sm">
            No certified-cycle samples yet (need wash orders that went through the full lifecycle in-app, not historical).
          </div>
        ) : (
          <div className="flex items-end gap-6">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">Average</div>
              <div className="font-mono font-black text-4xl text-[#006E9E]">
                {aging.avg_hours_req_to_cert.toFixed(1)}<span className="text-base ml-1 text-gray-500">hrs</span>
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">Samples</div>
              <div className="font-mono text-2xl">{aging.samples}</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
